import React from 'react';
import { observer } from 'mobx-react';
import { useFormikContext } from 'formik';
import { policyTypes } from '@ourbranch/policy-types';

import { ExpandableCard } from 'core/components/expandable-card';
import { useStore } from 'core/store/store.mobx';
import useBrandFeatures from 'core/hooks/use-brand-features';
import { useDiscounts } from 'offer/hooks/use-discounts';
import { MultilineDiscount } from './multiline-discount';
import { Discount } from './discount';
import { Paperless } from './paperless';
import { Inventory } from './inventory';
import { BranchPlusMultiPolicyDiscount } from './branch-plus-multi-policy-discount';
import { ConnectedHomeDiscount } from './connected-home-discount';
import { BundleDiscount } from './bundle-discount';
import { GoodStudent } from './good-student';
import { DrivingCourse } from './driving-course';
import { CommunityDrive } from './community-drive';
import { NewHomeDiscount } from './new-home-discount';
import { FutureEffectiveAdvancedQuote } from './future-effective-advanced-quote-savings';

const DiscountToComponent = {
  paperless_savings: Paperless,
  imagery_collection_savings: Inventory,
  connected_home_discount_savings: ConnectedHomeDiscount,
  multi_policy_savings: BranchPlusMultiPolicyDiscount,
  bundle_savings: BundleDiscount,
  good_student: GoodStudent,
  driving_course: DrivingCourse,
  community_drive: CommunityDrive,
  new_home_savings: NewHomeDiscount,
  future_effective_date_savings: FutureEffectiveAdvancedQuote
};

const DiscountsCard = observer(() => {
  const allDiscounts = useDiscounts();
  const { hasFutureEffectiveDateDiscount } = useBrandFeatures();

  // Filter out FED discount if brand doesn't support it
  const discounts = allDiscounts.filter((discount) => {
    if (discount.id === 'future_effective_date_savings' && !hasFutureEffectiveDateDiscount) {
      return false;
    }
    return true;
  });

  const activeDiscounts = discounts.filter(({ isActive }) => isActive).length;
  const { offer: store } = useStore();
  const { values } = useFormikContext();
  const selectedOption = values?.selectedOption || store.selectedOption;
  const bundleCurrentlySelected = [policyTypes.ARBundle, policyTypes.HABundle, policyTypes.CABundle].includes(
    selectedOption
  );

  const showMultilineDiscountComponent = store.hardRejectionOnOffer && !bundleCurrentlySelected;

  return (
    <div id="offer_discounts">
      <ExpandableCard title="Discounts" badge={`${activeDiscounts} of ${discounts.length}`}>
        {discounts.map(
          ({ canEdit, label, isActive, amount, description, possibleSavings, appcuesId, id, htmlId }, ix) => {
            const Component = DiscountToComponent[id] || Discount;
            return (
              <Component
                key={ix}
                label={label}
                amount={amount}
                possibleSavings={possibleSavings}
                description={description}
                isActive={isActive}
                appcuesId={appcuesId}
                canEdit={canEdit}
                id={id}
                htmlId={htmlId}
              />
            );
          }
        )}
        {showMultilineDiscountComponent && <MultilineDiscount />}
      </ExpandableCard>
    </div>
  );
});

export default DiscountsCard;
